import * as React from 'react';
const { connect } = require('react-redux');
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { IOrdersData, IOrdersAction } from 'models/orders/orders';
import { IStore } from 'redux/IStore';
import {
	setStatus,
	getShopOrders,
	resetOrdersData,
	setOrdersIsFirstLoad,
	setKeywordFilter,
} from 'redux/modules/shop/orders';
import { ErrorPage } from 'containers';
import { browserHistory } from 'react-router';
import { IModalAction } from 'models/modal';
import { IUserPreferences } from 'models/user/userpreferences';
import { closeModal, showModal } from 'redux/modules/modal';
import { OrderStatus } from 'models';
import { IOrderListItem } from 'models/orders/order';
import { SearchableTable } from 'components';
import { dateParser, formatCurrency } from 'helpers/common';

interface IProps {
	ordersData?: IOrdersData;
	params?: IParams;
	userPreferences?: IUserPreferences;
	setStatus?: (shopOrderId: string, shopId: string, status: OrderStatus, note: string) => IOrdersAction;
	showModal?: (modalType: string, modalProps: any) => IModalAction;
	closeModal?: () => IModalAction;
	getShopOrders?: (id: string, isFirstLoad: boolean, page?: number) => IOrdersAction;
	resetOrdersData?: () => IOrdersAction;
	setKeywordFilter?: (keyword: string) => IOrdersAction;
	t?: (key: string) => string;
}

interface IParams {
	id?: string;
}

interface IState {
	searchOpen: boolean;
}

@connect(
	(state: IStore) => ({
		ordersData: state.shopOrders,
		userPreferences: state.userPreferences,
	}),
	(dispatch) => ({
		getShopOrders: (id: string, isFirstLoad: boolean, page?: number) => dispatch(getShopOrders(id, isFirstLoad, page)),
		resetOrdersData: () => dispatch(resetOrdersData()),
		setOrdersIsFirstLoad: (isFirstLoad: boolean) => dispatch(setOrdersIsFirstLoad(isFirstLoad)),
		setStatus: (shopOrderId: string, shopId: string, status: OrderStatus, note: string) =>
			dispatch(setStatus(shopOrderId, shopId, status, note)),
		closeModal: () => dispatch(closeModal()),
		showModal: (modalType: string, modalProps: any) => dispatch(showModal(modalType, modalProps)),
		setKeywordFilter: (keyword: string) => dispatch(setKeywordFilter(keyword)),
	}),
)
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ShopOrders extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.handleOrderClicked = this.handleOrderClicked.bind(this);
		this.handleSearchValueChange = this.handleSearchValueChange.bind(this);
		this.handleClearSearchInput = this.handleClearSearchInput.bind(this);

		this.state = {
			searchOpen: false,
		};
	}

	public componentDidMount() {
		this.props.getShopOrders(this.props.params.id, true);
	}

	public componentDidUpdate(prevProps) {
		// If switching from one shop to another get the new shop's orders
		if (prevProps.params.id !== this.props.params.id) {
			this.props.resetOrdersData();
			this.props.getShopOrders(this.props.params.id, false);
		}
	}

	private handleViewMoreClick = () => {
		const currentPage = this.props.ordersData?.itemsPage?.currentPage;
		if (currentPage) {
			this.props.getShopOrders(this.props.params.id, false, currentPage + 1);
		}
	};

	private handleOrderClicked(order: IOrderListItem) {
		browserHistory.push(`/shop/${this.props.params.id}/edit/orders/order/${order.id}`);
	}

	private handleSearchValueChange(e: React.KeyboardEvent) {
		const value: string = (e.target as HTMLInputElement).value;
		this.props.setKeywordFilter(value);
	}

	private handleClearSearchInput() {
		this.props.setKeywordFilter(null);
	}

	public render() {
		const { t } = this.props;

		if (this.props.ordersData.error) {
			return <ErrorPage {...this.props} message={this.props.ordersData.message} />;
		}

		let orders: Array<IOrderListItem> = [];
		let totalOrders = 0;

		if (
			this.props.ordersData !== undefined ||
			this.props.ordersData !== null ||
			this.props.ordersData.items !== undefined
		) {
			orders = this.props.ordersData.items;
			if (this.props.ordersData?.itemsPage?.totalItems) totalOrders = this.props.ordersData.itemsPage.totalItems;
			else totalOrders = orders.length;
		}

		const areThereMoreElementsToLoad = (): boolean => {
			if (totalOrders > orders.length && orders.length >= c.SHOPCARD_TAB_ORDERS_ITEMSPERPAGE) {
				return true;
			}
			return false;
		};

		const headerArray = [
			{
				title: t('Orders_OrderCard_Label_Id'),
				size: 8,
				getter: (order: any) => order.id,
			},
			{
				title: t('BSPortal_Common_Date'),
				size: 2,
				sortable: true,
				defaultReversed: false,
				compareFnc: (order1: IOrderListItem, order2: IOrderListItem) =>
					new Date(order2.addedDate).getTime() - new Date(order1.addedDate).getTime(),
				getter: (order) => dateParser(order.addedDate),
			},
			{
				title: t('Orders_OrderCard_Label_Total'),
				size: 2,
				getter: (order: IOrderListItem) => order?.currencyKey && formatCurrency(order?.currencyKey, order?.total),
				className: 'text-right d-none d-sm-block',
			},
		];

		return (
			<div style={{ padding: 0 }}>
				<SearchableTable<IOrderListItem>
					areThereMoreElementsToLoad={areThereMoreElementsToLoad}
					columns={headerArray}
					filterFunction={(order, keyword) => order.id?.includes(keyword)}
					handleClearSearchInput={this.handleClearSearchInput}
					handleItemClicked={this.handleOrderClicked}
					handleSearchValueChange={this.handleSearchValueChange}
					handleViewMoreClick={this.handleViewMoreClick}
					items={orders}
					keywordFilter={this.props.ordersData?.keywordFilter}
					searchPlaceHolder={t('Shop_Orders_ShopOrders_Search_Placeholder')}
					totalItemsLabel={`${totalOrders} ${t('Shop_Orders_ShopOrders_PageTitle').toLowerCase()}`}
					viewMoreButtonLabel={t('Shop_ShopCard_ViewMoreOrders')}
					noItemsDescription={t('Shop_Orders_ShopOrders_NoOrdersDescription')}
					noItemsTitle={t('User_UserCard_NothingHereYet')}
				/>
			</div>
		);
	}
}

export { ShopOrders };
